"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "SimpleScheduler", {
    enumerable: true,
    get: function() {
        return SimpleScheduler;
    }
});
const _workerthreadspool = require("@lage-run/worker-threads-pool");
const _formatBytes = require("./formatBytes.js");
const _categorizeTargetRuns = require("./categorizeTargetRuns.js");
const _targetgraph = require("@lage-run/target-graph");
const _WrappedTarget = require("./WrappedTarget.js");
const _runners = require("@lage-run/runners");
function _check_private_redeclaration(obj, privateCollection) {
    if (privateCollection.has(obj)) {
        throw new TypeError("Cannot initialize the same private elements twice on an object");
    }
}
function _class_private_method_get(receiver, privateSet, fn) {
    if (!privateSet.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return fn;
}
function _class_private_method_init(obj, privateSet) {
    _check_private_redeclaration(obj, privateSet);
    privateSet.add(obj);
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
var _generateTargetRunPromise = /*#__PURE__*/ new WeakSet();
class SimpleScheduler {
    getTargetsByPriority() {
        return (0, _targetgraph.sortTargetsByPriority)([
            ...this.targetRuns.values()
        ].map((run)=>run.target));
    }
    /**
   * The job of the run method is to:
   * 1. Convert the target graph into a promise graph.
   * 2. Create a promise graph of all targets
   * 3. Pass the continueOnError option to the promise graph runner.
   *
   * @param root
   * @param targetGraph
   * @returns
   */ async run(root, targetGraph, shouldRerun = false) {
        const startTime = process.hrtime();
        const { continueOnError, logger, shouldCache } = this.options;
        logger.verbose("", {
            schedulerRun: {
                startTime
            }
        });
        const { pool, abortController } = this;
        const { targets } = targetGraph;
        for (const target of targets.values()){
            let targetRun;
            const prevTargetRun = this.targetRuns.get(target.id);
            if (prevTargetRun) {
                targetRun = prevTargetRun;
                // If previous run has been successful, then we may want to rerun
                if (prevTargetRun.successful && shouldRerun) {
                    this.markTargetAndDependentsPending(target.id);
                } else if (prevTargetRun.waiting && shouldRerun) {
                    this.rerunTargets.add(targetRun.target.id);
                } else if (!prevTargetRun.successful) {
                    // If previous run has failed, we should rerun
                    this.markTargetAndDependentsPending(target.id);
                }
            } else {
                targetRun = new _WrappedTarget.WrappedTarget({
                    target,
                    root,
                    logger,
                    shouldCache,
                    continueOnError,
                    abortController,
                    pool,
                    hasher: this.options.hasher,
                    onMessage: this.options.onMessage
                });
            }
            this.targetRuns.set(target.id, targetRun);
        }
        let results = "failed";
        let error;
        let duration = [
            0,
            0
        ];
        let targetRunByStatus;
        try {
            await this.scheduleReadyTargets();
        } catch (e) {
            error = e instanceof Error ? e.message : String(e);
        } finally{
            duration = process.hrtime(startTime);
            targetRunByStatus = (0, _categorizeTargetRuns.categorizeTargetRuns)(this.targetRuns.values());
            if (targetRunByStatus.failed.length + targetRunByStatus.aborted.length + targetRunByStatus.pending.length + targetRunByStatus.running.length === 0) {
                results = "success";
            }
        }
        const poolStats = pool.stats();
        await this.options.hasher.cleanup();
        return {
            targetRunByStatus,
            targetRuns: this.targetRuns,
            duration,
            startTime,
            results,
            error,
            workerRestarts: poolStats.workerRestarts,
            maxWorkerMemoryUsage: poolStats.maxWorkerMemoryUsage
        };
    }
    /**
   * Used by consumers of the scheduler to notify that the inputs to the target has changed
   * @param targetId
   */ markTargetAndDependentsPending(targetId) {
        const queue = [
            targetId
        ];
        while(queue.length > 0){
            const current = queue.shift();
            if (this.targetRuns.has(current)) {
                const targetRun = this.targetRuns.get(current);
                if (targetRun.status !== "pending") {
                    targetRun.reset();
                    this.rerunTargets.add(targetRun.target.id);
                    const dependents = targetRun.target.dependents;
                    for (const dependent of dependents){
                        queue.push(dependent);
                    }
                }
            }
        }
    }
    getReadyTargets() {
        const readyTargets = new Set();
        for (const target of this.getTargetsByPriority()){
            // Skip the start target
            if (target.id === (0, _targetgraph.getStartTargetId)()) {
                continue;
            }
            const targetRun = this.targetRuns.get(target.id);
            // If the target is already running, then we can't run it again
            if (targetRun.status === "pending") {
                const targetDeps = targetRun.target.dependencies;
                // Target is only ready when all its deps are "successful" or that it is a root target
                const ready = targetDeps.every((dep)=>{
                    const fromTarget = this.targetRuns.get(dep);
                    return fromTarget.successful || dep === (0, _targetgraph.getStartTargetId)();
                });
                if (ready) {
                    readyTargets.add(targetRun);
                }
            }
        }
        return [
            ...readyTargets
        ];
    }
    isAllDone() {
        for (const t of this.targetRuns.values()){
            if (t.status !== "skipped" && t.status !== "success" && t.target.id !== (0, _targetgraph.getStartTargetId)()) {
                return false;
            }
        }
        return true;
    }
    async scheduleReadyTargets() {
        if (this.isAllDone() || this.abortSignal.aborted) {
            return Promise.resolve();
        }
        this.options.logger.silly(`Max Worker Memory Usage: ${(0, _formatBytes.formatBytes)(this.pool.stats().maxWorkerMemoryUsage)}`);
        const promises = [];
        for (const nextTarget of this.getReadyTargets()){
            const runPromise = _class_private_method_get(this, _generateTargetRunPromise, generateTargetRunPromise).call(this, nextTarget);
            promises.push(runPromise);
        }
        await Promise.all(promises);
    }
    logProgress() {
        const targetRunByStatus = (0, _categorizeTargetRuns.categorizeTargetRuns)(this.targetRuns.values());
        const total = [
            ...this.targetRuns.values()
        ].filter((t)=>!t.target.hidden).length;
        this.options.logger.verbose("", {
            poolStats: this.pool.stats(),
            progress: {
                waiting: targetRunByStatus.pending.length + targetRunByStatus.queued.length,
                completed: targetRunByStatus.aborted.length + targetRunByStatus.failed.length + targetRunByStatus.skipped.length + targetRunByStatus.success.length,
                total
            }
        });
    }
    async cleanup() {
        this.options.logger.silly(`Max Worker Memory Usage: ${(0, _formatBytes.formatBytes)(this.pool.stats().maxWorkerMemoryUsage)}`);
        await this.pool.close();
    }
    /**
   * Abort the scheduler using the abort controller.
   */ abort() {
        this.abortController.abort();
    }
    constructor(options){
        _class_private_method_init(this, _generateTargetRunPromise);
        _define_property(this, "options", void 0);
        _define_property(this, "targetRuns", void 0);
        _define_property(this, "rerunTargets", void 0);
        _define_property(this, "abortController", void 0);
        _define_property(this, "abortSignal", void 0);
        _define_property(this, "pool", void 0);
        _define_property(this, "runnerPicker", void 0);
        _define_property(this, "runPromise", void 0);
        this.options = options;
        this.targetRuns = new Map();
        this.rerunTargets = new Set();
        this.abortController = new AbortController();
        this.abortSignal = this.abortController.signal;
        this.runPromise = Promise.resolve();
        this.pool = options.pool ?? new _workerthreadspool.AggregatedPool({
            logger: options.logger,
            maxWorkersByGroup: options.maxWorkersPerTask,
            groupBy: ({ target })=>target.task,
            maxWorkers: options.concurrency,
            script: require.resolve("./workers/targetWorker"),
            workerOptions: {
                stdout: true,
                stderr: true,
                workerData: {
                    ...options.workerData,
                    shouldCache: options.shouldCache,
                    shouldResetCache: options.shouldResetCache
                }
            },
            workerIdleMemoryLimit: options.workerIdleMemoryLimit
        });
        this.runnerPicker = new _runners.TargetRunnerPicker(options.workerData.runners);
    }
}
async function generateTargetRunPromise(target) {
    let runError;
    if (!target.successful || this.rerunTargets.has(target.target.id)) {
        // This do-while loop only runs again if something causes this target to rerun (asynchronously triggering a re-run)
        do {
            this.rerunTargets.delete(target.target.id);
            target.onQueued();
            try {
                let shouldRun = true;
                try {
                    const runner = await this.runnerPicker.pick(target.target);
                    shouldRun = await runner.shouldRun(target.target);
                } catch (e) {
                // pass - default to run anyway
                }
                if (shouldRun) {
                    await target.run();
                } else {
                    target.onSkipped();
                }
            } catch (e) {
                runError = e;
            }
        }while (this.rerunTargets.has(target.target.id))
        // if a continue option is set, this merely records what errors have been encountered
        // it'll continue down the execution until all the tasks that still works
        if (runError && !this.options?.continueOnError) {
            if (!this.options?.continueOnError) {
                // immediately reject, if not using "continue" option
                throw runError;
            }
        }
    }
    this.logProgress();
    // finally do another round of scheduling to run next round of targets
    await this.scheduleReadyTargets();
}
encodeURI;
